import type { TaskListItemDto } from '@ovo/shared'
import type { ReactNode } from 'react'
import { Link } from 'react-router'
import { isOpen, REQUEST_LABELS, shortName, statusLabel } from '../lib/status.ts'
import { DeadlineLabel } from './DeadlineLabel.tsx'
import { IconUser } from './icons.tsx'
import { PriorityMark } from './PriorityMark.tsx'
import { StatusGlyph } from './StatusBadge.tsx'

interface TaskCellProps {
  task: TaskListItemDto
  meId: number
  /** Чьё имя показывать первым: исполнителя (для «Поставил») или постановщика (для «Мои»). */
  lead?: 'assignee' | 'creator'
  now: Date
  /** Тап по значку статуса — шторка действий. Без обработчика значок не интерактивен. */
  onGlyph?: (task: TaskListItemDto) => void
  /** Кнопка справа (например, «Принять»). */
  action?: ReactNode
}

export function TaskCell({ task, meId, lead = 'creator', now, onGlyph, action }: TaskCellProps) {
  const closed = !isOpen(task.status)
  const label = statusLabel(task.status)
  const showCreator = lead === 'creator' && task.creator.id !== meId
  const showAssignee = lead === 'assignee' && task.assignee.id !== meId

  return (
    <div className="task-cell">
      {onGlyph ? (
        <button
          type="button"
          className="task-cell__glyph"
          aria-label={`${label}. Изменить статус задачи «${task.title}»`}
          onClick={() => onGlyph(task)}
        >
          <StatusGlyph status={task.status} />
        </button>
      ) : (
        <span className="task-cell__glyph" role="img" aria-label={label}>
          <StatusGlyph status={task.status} />
        </span>
      )}
      <Link to={`/tasks/${task.id}`} className="task-cell__body">
        {showAssignee && <span className="task-cell__lead">{task.assignee.displayName}</span>}
        <span className="task-cell__title-row">
          <span className={`task-cell__title${closed ? ' task-cell__title--closed' : ''}`}>
            <span className={closed && task.status !== 'done' ? 'strike' : undefined}>{task.title}</span>
          </span>
          <PriorityMark priority={task.priority} />
        </span>
        <span className="task-cell__meta">
          {closed ? (
            <span>{label}</span>
          ) : task.status === 'submitted' ? (
            <span className="text-done">На проверке</span>
          ) : (
            <DeadlineLabel
              dueAt={task.dueAt}
              dueKind={task.dueKind}
              status={task.status}
              isOverdue={task.isOverdue}
              now={now}
            />
          )}
          {showCreator && task.creator.id === task.assignee.id ? (
            // Сотрудник поставил задачу себе сам (так её видит руководитель)
            <span className="tag" title="Сотрудник поставил задачу себе сам">
              🙋 себе
            </span>
          ) : showCreator ? (
            <span className="task-cell__person" title="Постановщик">
              <IconUser size={13} strokeWidth={2} />
              {shortName(task.creator.displayName)}
            </span>
          ) : null}
          {task.pendingRequest && (
            <span className={`tag${task.pendingRequest === 'question' ? '' : ' tag--attn'}`}>
              {REQUEST_LABELS[task.pendingRequest]}
            </span>
          )}
          {task.status === 'blocked' && <span className="tag tag--attn">Блокер</span>}
        </span>
      </Link>
      {action && <div className="task-cell__action">{action}</div>}
    </div>
  )
}
